import { Suspense, lazy, useMemo } from 'react'

import { useQuery } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { ShowPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import Attachments, { FileLike } from 'modules/inbox_tasks/components/Attachments'
import { useTaskDetails } from 'modules/tasks/components/TaskElement/useTaskDetail'

interface DetailsTabProps {
  taskId: string
  content?: string
  files?: FileLike[]
}

const DetailsTab = ({ taskId, files }: DetailsTabProps) => {
  const { company } = useCurrentSession()

  const {
    task,
    loading,
    error,
    canSeePersonalInfo,
    canEditPersonalInfo,
    isVersionDataEnabled,
    hasDevTimeTrackingWorkableDaysDefinition,
    hasTimeTrackingAnnualisationDailyValue,
    referenceContract,
    jobCatalogContextlessPolicies,
    hasPayrollBundle,
    contractContextlessPolicies,
    customFieldsContextlessPolicies,
  } = useTaskDetails({
    ids: [taskId],
    companyId: company?.id,
  })

  const TaskDetailsContent = useMemo(
    () => lazy(() => import('modules/tasks/components/TaskDetails/Content')),
    []
  )

  const { data } = useQuery(ShowPageDocument, {
    variables: {
      taskId,
    },
  })

  const canEmployeeManageAnyEmployeeFolder =
    data?.documents._policies.employeeManageAnyEmployeeFolder.can ?? false

  const customResourceFieldsData =
    data?.customFields.resourceFieldsConnection.edges.map((edge) => edge.node).filter(Boolean) ?? []

  const customValuesData =
    data?.tasks.tasksConnection.edges[0]?.node?.affecteesConnection.edges[0]?.node?.employee?.valuesConnection.edges.map(
      (edge) => edge.node
    ) ?? []

  if (!task)
    return loading ? (
      <Loading />
    ) : error ? (
      <OneEmptyState
        variant="critical"
        title={i18n.t('common.error')}
        description={i18n.t('common.error')}
      />
    ) : null

  return (
    <Box flexDirection="column" gap="s32">
      <Suspense fallback={<Loading />}>
        <TaskDetailsContent
          task={task}
          owner={undefined}
          canSeePersonalInfo={canSeePersonalInfo}
          canEditPersonalInfo={canEditPersonalInfo}
          canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
          isVersionDataEnabled={isVersionDataEnabled}
          customResourceFieldsData={customResourceFieldsData}
          customValuesData={customValuesData}
          hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
          hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
          referenceContract={referenceContract}
          jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
          hasPayrollBundle={hasPayrollBundle}
          contractContextlessPolicies={contractContextlessPolicies}
          customFieldsContextlessPolicies={customFieldsContextlessPolicies}
          hideComments
        />
      </Suspense>
      {!!files?.length && (
        <Box flexDirection="column" gap="s16">
          <Text.Header3>
            {i18n.t('processes.process_settings.wizard.tasks.preview.attachments')}
          </Text.Header3>
          <Attachments files={files} />
        </Box>
      )}
    </Box>
  )
}

export default DetailsTab
